import { useState, useRef, useLayoutEffect, forwardRef } from 'react';
import { clampToViewport } from '@/hooks/useDropdownPosition';
import {
  ChevronRight,
  CheckCircle2,
  CircleDashed,
  AlertTriangle,
  Flag,
  Copy,
  Trash2,
  CircleDot,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { useClickOutside } from '@/hooks/useClickOutside';
import { TASK_STATUS, getStatusLabel } from '@/constants/taskStatuses';
import type { Task, Priority } from '@/types';

interface TaskContextMenuProps {
  x: number;
  y: number;
  task: Task;
  onStatusChange: (status: string) => void;
  onPriorityChange: (priority: Priority | null) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onClose: () => void;
}

const statusOptions = [
  { value: TASK_STATUS.OPEN, icon: CircleDashed, color: 'text-gray-500' },
  { value: TASK_STATUS.IN_PROGRESS, icon: CircleDot, color: 'text-blue-500' },
  { value: TASK_STATUS.BLOCKED, icon: AlertTriangle, color: 'text-red-500' },
  { value: TASK_STATUS.COMPLETED, icon: CheckCircle2, color: 'text-green-500' },
  { value: TASK_STATUS.CANCELLED, icon: XCircle, color: 'text-gray-400' },
];

const priorityOptions: Array<{ value: Priority | null; label: string; color: string }> = [
  { value: 'urgent', label: 'Urgente', color: 'text-red-500' },
  { value: 'high', label: 'Alta', color: 'text-orange-500' },
  { value: 'normal', label: 'Normale', color: 'text-blue-500' },
  { value: 'low', label: 'Bassa', color: 'text-gray-500' },
  { value: null, label: 'Nessuna', color: 'text-gray-400' },
];

export const TaskContextMenu = forwardRef<HTMLDivElement, TaskContextMenuProps>(
  ({ x, y, task, onStatusChange, onPriorityChange, onDuplicate, onDelete, onClose }, ref) => {
    const [activeSubmenu, setActiveSubmenu] = useState<'status' | 'priority' | null>(null);
    const submenuRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState({ left: x, top: y });

    useLayoutEffect(() => {
      const el = menuRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos(clampToViewport(x, y, rect.width, rect.height));
    }, [x, y]);

    useClickOutside([submenuRef], () => setActiveSubmenu(null), activeSubmenu !== null);

    return (
      <div
        ref={(node) => {
          (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className="fixed z-9999 min-w-[180px] bg-surface-secondary border border-border-primary rounded-xl shadow-2xl overflow-visible animate-scale-in origin-top-left"
        style={{ left: pos.left, top: pos.top }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-3 py-2 border-b border-border-primary bg-surface-tertiary/30">
          <div className="text-[10px] text-text-quaternary uppercase tracking-wider font-semibold">
            Task
          </div>
          <div className="text-[11px] font-medium text-text-primary truncate mt-0.5">
            {task.name}
          </div>
        </div>

        {/* Menu Items */}
        <div className="py-1 relative">
          {/* Status submenu */}
          <div
            className="relative"
            onMouseEnter={() => setActiveSubmenu('status')}
            onMouseLeave={(e) => {
              if (!submenuRef.current?.contains(e.relatedTarget as Node)) {
                setActiveSubmenu(null);
              }
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveSubmenu(activeSubmenu === 'status' ? null : 'status');
              }}
              className={clsx(
                'w-full flex items-center justify-between gap-2.5 px-3 py-2 text-[11px] transition-colors',
                'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                activeSubmenu === 'status' && 'bg-surface-hover'
              )}
            >
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span className="font-medium">Stato</span>
              </div>
              <ChevronRight className={clsx('w-3 h-3 transition-transform', activeSubmenu === 'status' && 'rotate-90')} />
            </button>
            {activeSubmenu === 'status' && (
              <div
                ref={submenuRef}
                data-submenu="true"
                className="absolute top-0 ml-1 min-w-[160px] bg-surface-secondary border border-border-primary rounded-xl shadow-2xl overflow-hidden z-10000"
                style={{ left: pos.left + 180 + 160 > window.innerWidth ? undefined : '100%', right: pos.left + 180 + 160 > window.innerWidth ? '100%' : undefined }}
                onMouseEnter={() => setActiveSubmenu('status')}
                onMouseLeave={() => setActiveSubmenu(null)}
              >
                <div className="py-1">
                  {statusOptions.map((option) => {
                    const Icon = option.icon;
                    const isActive = task.status === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onStatusChange(option.value);
                          onClose();
                        }}
                        className={clsx(
                          'w-full flex items-center gap-2.5 px-3 py-2 text-[11px] transition-colors',
                          isActive
                            ? 'bg-surface-hover text-text-primary font-medium'
                            : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                        )}
                      >
                        <Icon className={clsx('w-3.5 h-3.5', option.color)} />
                        <span>{getStatusLabel(option.value)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Priority submenu */}
          <div
            className="relative"
            onMouseEnter={() => setActiveSubmenu('priority')}
            onMouseLeave={(e) => {
              if (!submenuRef.current?.contains(e.relatedTarget as Node)) {
                setActiveSubmenu(null);
              }
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveSubmenu(activeSubmenu === 'priority' ? null : 'priority');
              }}
              className={clsx(
                'w-full flex items-center justify-between gap-2.5 px-3 py-2 text-[11px] transition-colors',
                'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                activeSubmenu === 'priority' && 'bg-surface-hover'
              )}
            >
              <div className="flex items-center gap-2.5">
                <Flag className="w-3.5 h-3.5" />
                <span className="font-medium">Priorità</span>
              </div>
              <ChevronRight className={clsx('w-3 h-3 transition-transform', activeSubmenu === 'priority' && 'rotate-90')} />
            </button>
            {activeSubmenu === 'priority' && (
              <div
                ref={submenuRef}
                data-submenu="true"
                className="absolute top-0 ml-1 min-w-[140px] bg-surface-secondary border border-border-primary rounded-xl shadow-2xl overflow-hidden z-10000"
                style={{ left: pos.left + 180 + 140 > window.innerWidth ? undefined : '100%', right: pos.left + 180 + 140 > window.innerWidth ? '100%' : undefined }}
                onMouseEnter={() => setActiveSubmenu('priority')}
                onMouseLeave={() => setActiveSubmenu(null)}
              >
                <div className="py-1">
                  {priorityOptions.map((option) => {
                    const isActive = (task.priority ?? null) === option.value;
                    return (
                      <button
                        key={option.value ?? 'none'}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onPriorityChange(option.value);
                          onClose();
                        }}
                        className={clsx(
                          'w-full flex items-center gap-2.5 px-3 py-2 text-[11px] transition-colors',
                          isActive
                            ? 'bg-surface-hover text-text-primary font-medium'
                            : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                        )}
                      >
                        <Flag className={clsx('w-3.5 h-3.5', option.color)} />
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="h-px bg-border-primary my-1 mx-2" />

          {/* Duplicate */}
          <button
            onClick={() => {
              onDuplicate();
              onClose();
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[11px] transition-colors text-text-secondary hover:bg-surface-hover hover:text-text-primary"
          >
            <Copy className="w-3.5 h-3.5" />
            <span className="font-medium">Duplica</span>
          </button>

          <div className="h-px bg-border-primary my-1 mx-2" />

          {/* Delete */}
          <button
            onClick={() => {
              onDelete();
              onClose();
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[11px] transition-colors text-accent-rose hover:bg-accent-rose/10"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="font-medium">Elimina</span>
          </button>
        </div>

        {/* Footer hint */}
        <div className="px-3 py-1.5 border-t border-border-primary bg-surface-tertiary/20">
          <div className="text-[9px] text-text-quaternary">
            Premi <kbd className="kbd text-[8px]">Esc</kbd> per chiudere
          </div>
        </div>
      </div>
    );
  }
);

TaskContextMenu.displayName = 'TaskContextMenu';
